import { useState, useCallback } from "react";
import ApiClient from "@/lib/api-client";

export interface UploadedFile {
  id: string;
  name: string;
  url: string;
  key: string;
  contentType: string;
}

export interface UseFileUploadOptions {
  uploadEndpoint: string;
  getUploadParams: (file: File) => Record<string, unknown>;
  onUploadComplete?: (file: UploadedFile) => void;
  onUploadError?: (error: Error, fileName: string) => void;
}

export interface UseFileUploadReturn {
  isUploading: boolean;
  uploadProgress: Map<string, number>;
  error: string | null;
  uploadFiles: (files: FileList | File[]) => Promise<UploadedFile[]>;
  clearError: () => void;
}

export function useFileUpload({
  uploadEndpoint,
  getUploadParams,
  onUploadComplete,
  onUploadError,
}: UseFileUploadOptions): UseFileUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const uploadSingleFile = async (file: File): Promise<UploadedFile> => {
    const fileId = crypto.randomUUID();

    setUploadProgress((prev) => new Map(prev).set(fileId, 0));

    const { upload_url, key, public_url } = await ApiClient.post<{
      upload_url: string;
      key: string;
      public_url: string;
    }>(uploadEndpoint, getUploadParams(file));

    setUploadProgress((prev) => new Map(prev).set(fileId, 50));

    const uploadResponse = await fetch(upload_url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload ${file.name}`);
    }

    setUploadProgress((prev) => new Map(prev).set(fileId, 100));

    const uploadedFile: UploadedFile = {
      id: fileId,
      name: file.name,
      url: public_url,
      key,
      contentType: file.type,
    };

    return uploadedFile;
  };

  const uploadFiles = useCallback(
    async (files: FileList | File[]): Promise<UploadedFile[]> => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return [];

      setIsUploading(true);
      setError(null);

      const uploadedFiles: UploadedFile[] = [];

      for (const file of fileArray) {
        try {
          const uploaded = await uploadSingleFile(file);
          uploadedFiles.push(uploaded);
          onUploadComplete?.(uploaded);
        } catch (err) {
          const error = err instanceof Error ? err : new Error("Upload failed");
          setError(error.message);
          onUploadError?.(error, file.name);
        }
      }

      setIsUploading(false);
      setUploadProgress(new Map());

      return uploadedFiles;
    },
    [uploadEndpoint, getUploadParams, onUploadComplete, onUploadError]
  );

  return {
    isUploading,
    uploadProgress,
    error,
    uploadFiles,
    clearError,
  };
}
